import { memo } from "react";
import {
  ColumnDef,
  ColumnSizingState,
  SortingState,
  VisibilityState,
  flexRender,
} from "@tanstack/react-table";
import {
  ArrowUpNarrowWideIcon,
  ArrowDownWideNarrowIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useCreatorsTableContext } from "./CreatorsTableContext";
import { Creator } from "@/types/types";

/**
 * Everything the header shows is read through the table instance; these
 * props exist so the memo below re-renders exactly when that output can
 * change (column set, sort indicators, hidden columns, sizes) and not on
 * every keystroke / loading flip of the table around it.
 */
interface CreatorsTableHeaderProps {
  columns: ColumnDef<Creator>[];
  sorting: SortingState;
  columnVisibility: VisibilityState;
  columnSizing: ColumnSizingState;
}

function CreatorsTableHeader(keys: CreatorsTableHeaderProps) {
  void keys; // memo comparison keys only — the header renders from the table instance
  const { table } = useCreatorsTableContext();

  return (
    <TableHeader className="h-10">
      {table.getHeaderGroups().map((headerGroup) => (
        // Row borders don't travel with sticky cells under border-collapse —
        // the header line is a shadow on the th's instead (border-b-0!).
        <TableRow key={headerGroup.id} className="hover:bg-transparent border-b-0!">
          {headerGroup.headers.map((header) => {
            return (
              <TableHead
                key={header.id}
                style={{ width: `${header.getSize()}px` }}
                // Sticky top row of the white panel — solid bg so rows slide
                // under it, hairline via shadow so the line sticks with it.
                className="h-10 sticky top-0 z-10 bg-background shadow-[0_1px_0_0_var(--border)] border-b"
              >
                {header.isPlaceholder ? null : header.column.getCanSort() ? (
                  <div
                    className={cn(
                      header.column.getCanSort() &&
                        "flex h-full cursor-pointer items-center justify-between gap-2 select-none hover:text-foreground"
                    )}
                    onClick={header.column.getToggleSortingHandler()}
                    onKeyDown={(e) => {
                      if (
                        header.column.getCanSort() &&
                        (e.key === "Enter" || e.key === " ")
                      ) {
                        e.preventDefault();
                        header.column.getToggleSortingHandler()?.(e);
                      }
                    }}
                    tabIndex={header.column.getCanSort() ? 0 : undefined}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {{
                      asc: (
                        <ArrowUpNarrowWideIcon
                          className="shrink-0 opacity-80"
                          size={16}
                          aria-hidden="true"
                        />
                      ),
                      desc: (
                        <ArrowDownWideNarrowIcon
                          className="shrink-0 opacity-80"
                          size={16}
                          aria-hidden="true"
                        />
                      ),
                    }[header.column.getIsSorted() as string] ?? null}
                  </div>
                ) : (
                  flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )
                )}
              </TableHead>
            );
          })}
        </TableRow>
      ))}
    </TableHeader>
  );
}

export default memo(CreatorsTableHeader);
